import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { execute } from '@/graphql/execute';
import { updateProjectLinkPasswordMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface ProjectLinkPasswordDialogProps {
  linkId: string;
  linkName: string;
  hasPassword: boolean;
  projectId: string;
}

export function ProjectLinkPasswordDialog({ linkId, linkName, hasPassword, projectId }: ProjectLinkPasswordDialogProps) {
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: async (newPassword: string | null) => {
      return execute(updateProjectLinkPasswordMutation, { id: linkId, password: newPassword });
    },
    onSuccess: (_, newPassword) => {
      queryClient.invalidateQueries({ queryKey: ['projectLinks', projectId] });
      toast.success(newPassword ? 'Password updated' : 'Password removed');
      dismissActiveDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update password');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    mutation.mutate(password);
  };

  const handleRemove = () => {
    mutation.mutate(null);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{hasPassword ? 'Update Password' : 'Set Password'}</DialogTitle>
        <DialogDescription>
          {hasPassword
            ? `Change or remove the password for "${linkName}".`
            : `Set a password for "${linkName}". Users will need to enter it before accessing the shared content.`}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Input
            type='password'
            placeholder='Enter new password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={mutation.isPending}
            autoFocus
          />
        </div>

        <DialogFooter>
          {hasPassword && (
            <Button
              type='button'
              variant='outline'
              className='text-destructive hover:text-destructive'
              disabled={mutation.isPending}
              onClick={handleRemove}>
              {mutation.isPending ? <Loader2 className='size-4 animate-spin' /> : 'Remove Password'}
            </Button>
          )}
          <Button type='button' variant='outline' onClick={dismissActiveDialog} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type='submit' disabled={mutation.isPending || !password}>
            {mutation.isPending ? <Loader2 className='size-4 animate-spin' /> : hasPassword ? 'Update' : 'Set Password'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
